import { Menu } from 'primeng/menu'
import { Overlay } from 'primeng/overlay'
import { Popover } from 'primeng/popover'
import { TieredMenu } from 'primeng/tieredmenu'

interface Placement {
  anchor: Element
  /** Set once the popup sits above its anchor: the gap kept under it while it resizes or the insets change. */
  gap?: number
}

const placements = new Map<HTMLElement, Placement>()
let resizeObserver: ResizeObserver | undefined
let insetProbe: HTMLElement | undefined

/**
 * Keeps PrimeNG popups clear of the bottom system bar and the on-screen keyboard.
 *
 * PrimeNG places a popup below its trigger when it fits in the window and flips it above otherwise. Apps that draw
 * edge to edge (fleet-mobile) run the window under the system bars and let the keyboard overlay it, so a popup that
 * fits the window can still end under the bar or the keyboard. After each alignment such a popup moves above its
 * trigger. Open popups are checked again when the keyboard height changes, and popups above their trigger keep their
 * bottom edge there when their content grows. Apps without the `--overlay-inset-*` tokens keep PrimeNG's placement.
 */
export function patchOverlayInsets() {
  // Select, MultiSelect, AutoComplete and other p-overlay popups; also runs when they realign while filtering
  const orig__Overlay_alignOverlay = Overlay.prototype.alignOverlay
  Overlay.prototype.alignOverlay = function (...args) {
    orig__Overlay_alignOverlay.call(this, ...args)
    if (!this.modal) {
      track(this.overlayEl, this.targetEl)
    }
  }

  const orig__Menu_onOverlayBeforeEnter = Menu.prototype.onOverlayBeforeEnter
  Menu.prototype.onOverlayBeforeEnter = function (...args) {
    orig__Menu_onOverlayBeforeEnter.call(this, ...args)
    track(this.container, this.target)
  }

  const orig__TieredMenu_alignOverlay = TieredMenu.prototype.alignOverlay
  TieredMenu.prototype.alignOverlay = function (...args) {
    orig__TieredMenu_alignOverlay.call(this, ...args)
    track(this.container, this.target)
  }

  const orig__Popover_align = Popover.prototype.align
  Popover.prototype.align = function (...args) {
    orig__Popover_align.call(this, ...args)
    track(this.container, this.target)
  }

  // Menu items can arrive after the menu opened, and PrimeNG doesn't realign then
  resizeObserver = new ResizeObserver((entries) =>
    entries.forEach((entry) => refresh(entry.target as HTMLElement)),
  )
  // KeyboardOverlayHandler writes the keyboard height to the root element's style
  new MutationObserver(() => placements.forEach((_, popup) => refresh(popup))).observe(
    document.documentElement,
    { attributes: true, attributeFilter: ['style'] },
  )
}

function track(popup: HTMLElement | null | undefined, anchor: Element | null | undefined) {
  if (!(popup instanceof HTMLElement) || !(anchor instanceof Element)) {
    return
  }
  let placement = placements.get(popup)
  if (placement?.anchor !== anchor) {
    placement = { anchor }
    placements.set(popup, placement)
    resizeObserver?.observe(popup)
  }
  placement.gap = placeAboveInsets(popup, anchor, placement.gap)
}

function refresh(popup: HTMLElement) {
  const placement = placements.get(popup)
  if (!placement) {
    return
  }
  if (!popup.isConnected || !placement.anchor.isConnected) {
    placements.delete(popup)
    resizeObserver?.unobserve(popup)
  } else if (popup.getClientRects().length) {
    placement.gap = placeAboveInsets(popup, placement.anchor, placement.gap)
  }
}

/**
 * Moves a popup that ends under the bottom inset above its anchor, or as high as the top inset allows. With a kept
 * gap the popup stays above its anchor. Returns the gap to keep while the popup is above its anchor.
 */
export function placeAboveInsets(popup: HTMLElement, anchor: Element, keptGap?: number) {
  const insets = readOverlayInsets()
  if (!insets.bottom) {
    return undefined
  }

  const limit = window.innerHeight - insets.bottom
  const { top, height } = measure(popup)
  const target = anchor.getBoundingClientRect()
  if (keptGap === undefined && top + height <= limit + 0.5) {
    // PrimeNG flipped it above its anchor itself when there was no room below; keep its bottom edge there
    return popup.style.transformOrigin.includes('bottom') ? target.top - (top + height) : undefined
  }

  // Keep the gap PrimeNG left between the popup and its anchor
  const gap =
    keptGap ??
    Math.min(
      16,
      Math.max(0, top >= target.bottom ? top - target.bottom : target.top - (top + height)),
    )
  const bottom = Math.min(target.top - gap, limit)
  const newTop = Math.max(insets.top, bottom - height)
  const above = newTop + height <= target.top + 0.5

  const { top: inlineTop, transformOrigin } = popup.style
  moveBy(popup, newTop - top)
  popup.style.transformOrigin = above ? 'bottom' : 'top'
  // Some phone layouts position the popup from the stylesheet (a panel pinned to the bottom) and own its keyboard
  // handling; top has no effect there
  if (Math.abs(newTop - top) >= 0.5 && Math.abs(measure(popup).top - top) < 0.5) {
    popup.style.top = inlineTop
    popup.style.transformOrigin = transformOrigin
    return undefined
  }

  // A popover points its arrow at the anchor; flipped, the arrow moves to the bottom edge and the margins swap
  if (
    above &&
    popup.classList.contains('p-popover') &&
    !popup.classList.contains('p-popover-flipped')
  ) {
    const before = popup.getBoundingClientRect().bottom
    popup.setAttribute('data-p-popover-flipped', 'true')
    popup.classList.add('p-popover-flipped')
    moveBy(popup, before - popup.getBoundingClientRect().bottom)
  }
  return above ? gap : undefined
}

function moveBy(popup: HTMLElement, delta: number) {
  if (Math.abs(delta) >= 0.5) {
    popup.style.top = `${(parseFloat(popup.style.top) || 0) + delta}px`
  }
}

/** The `--overlay-inset-*` tokens in px; 0 where an app doesn't define them. */
function readOverlayInsets() {
  if (!insetProbe?.isConnected) {
    insetProbe = document.createElement('div')
    insetProbe.setAttribute('aria-hidden', 'true')
    insetProbe.style.cssText =
      'position: fixed; top: 0; left: 0; width: 0; height: 0; visibility: hidden; pointer-events: none; ' +
      'padding: var(--overlay-inset-top, 0px) 0 var(--overlay-inset-bottom, 0px)'
    document.body.appendChild(insetProbe)
  }
  const style = getComputedStyle(insetProbe)
  return { top: parseFloat(style.paddingTop) || 0, bottom: parseFloat(style.paddingBottom) || 0 }
}

/**
 * Top in the viewport and layout height. The enter animation scales the popup around the edge facing its anchor,
 * which stays exact; a popup about to enter may still be hidden.
 */
function measure(popup: HTMLElement) {
  const hidden = !popup.getClientRects().length
  const { visibility, display } = popup.style
  if (hidden) {
    popup.style.visibility = 'hidden'
    popup.style.display = 'block'
  }
  const rect = popup.getBoundingClientRect()
  const height = popup.offsetHeight
  if (hidden) {
    popup.style.visibility = visibility
    popup.style.display = display
  }
  return {
    top: popup.style.transformOrigin.includes('bottom') ? rect.bottom - height : rect.top,
    height,
  }
}
